import type { MondayBriefApiResponse, MondayBriefUi, MondayBriefUiItem } from '../types/profitabilityBrief';

const PROFIT_AGENT_BRIEF_PLACEHOLDERS = {
  customer: 'Crestwood Manufacturing Ltd',
  vehicle: 'AB12XYZ',
} as const;

function itemKindFromType(type: string): MondayBriefUiItem['kind'] {
  if (type.includes('customer')) return 'customer';
  if (type.includes('vehicle')) return 'vehicle';
  return 'overall';
}

function maskForProfitAgent(
  text: string,
  itemType: string,
  evidence: Record<string, unknown> | undefined,
  mask: boolean,
): { text: string; highlights: string[]; kind: MondayBriefUiItem['kind'] } {
  const kind = itemKindFromType(itemType);

  const rawCustomer = String(evidence?.customer ?? '').trim();
  const rawVehicle = String(evidence?.vehicle ?? '').trim();

  if (!mask) {
    const highlights = [rawCustomer, rawVehicle].filter(Boolean);
    return { text, highlights, kind };
  }

  let out = text;
  const highlights: string[] = [];
  if (rawCustomer) {
    out = out.split(rawCustomer).join(PROFIT_AGENT_BRIEF_PLACEHOLDERS.customer);
    highlights.push(PROFIT_AGENT_BRIEF_PLACEHOLDERS.customer);
  }
  if (rawVehicle) {
    out = out.split(rawVehicle).join(PROFIT_AGENT_BRIEF_PLACEHOLDERS.vehicle);
    highlights.push(PROFIT_AGENT_BRIEF_PLACEHOLDERS.vehicle);
  }
  return { text: out, highlights, kind };
}

function briefHeaderDisplayText(apiHeader: string | undefined, prettyEnd: string): string {
  if (!apiHeader?.trim()) return prettyEnd || '—';
  const parts = apiHeader
    .split('|')
    .map((p) => p.trim())
    .filter(Boolean)
    .filter((p) => !/^MONDAY BRIEF$/i.test(p));
  if (parts.length === 0) return prettyEnd || '—';
  const last = parts[parts.length - 1];
  return /\d/.test(last) ? last : prettyEnd || last;
}

export function mapMondayBriefApiToUi(
  api: MondayBriefApiResponse,
  opts: { maskEntities?: boolean; selectedYm?: string },
): MondayBriefUi {
  const from = api.analysis_window?.from ?? '';
  const to = api.analysis_window?.to ?? '';
  const monthWindow =
    from && to ? { startDate: from, endDate: to } : null;
  const selectedYm = opts.selectedYm ?? (from ? from.slice(0, 7) : '');

  const prettyEnd = (() => {
    if (!to) return '';
    const parts = to.split('-');
    const y = Number(parts[0]);
    const m1 = Number(parts[1]);
    const d = Number(parts[2]);
    if (!Number.isFinite(y) || !Number.isFinite(m1) || !Number.isFinite(d)) return to;
    const names = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return `${d} ${names[m1 - 1] ?? m1} ${y}`;
  })();

  const items: MondayBriefUiItem[] = (api.items ?? []).map((it) => {
    const evidence = it.evidence as Record<string, unknown> | undefined;
    const itemType = String(it.type ?? '');
    const maskedTitle = maskForProfitAgent(it.title ?? '', itemType, evidence, !!opts.maskEntities);
    const maskedBody = maskForProfitAgent(it.observation ?? '', itemType, evidence, !!opts.maskEntities);
    const maskedAction = maskForProfitAgent(it.recommended_action ?? '', itemType, evidence, !!opts.maskEntities);
    return {
      kind: maskedTitle.kind,
      highlights: maskedTitle.highlights,
      title: maskedTitle.text,
      body: maskedBody.text,
      action: maskedAction.text,
    };
  });

  const conf = api.confidence?.confidence ?? 'Medium';

  return {
    items,
    headerText: opts.maskEntities ? prettyEnd || '—' : briefHeaderDisplayText(api.header, prettyEnd),
    introText: 'Three things need your attention this week.',
    confidenceText: `Confidence: ${conf}.`,
    meta: { monthWindow, selectedYm },
    source: 'api',
  };
}

export {
  PROFIT_DRILLDOWN_STORAGE_KEY,
  type StoredProfitDrilldown,
  type CustomerProfitSortField,
  buildStoredProfitDrilldown,
  readStoredProfitDrilldown,
  writeStoredProfitDrilldown,
  isCustomerProfitabilityQuestion,
} from './profitDrilldownNavigation';
